import { Fragment, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Input from '../ui/Input';
import Badge from '../ui/Badge';
import {
  CATALOG_FACET_KEYS,
  type CatalogFacetKey,
  type CatalogFacets,
} from '../../types/resourceCatalog';
import { catalogLabel } from '../../utils/catalogLabels';

export interface ProgramFacetOption {
  slug: string;
  title: string;
  count: number;
}

const GROUPS: { key: CatalogFacetKey; label: string; startOpen: boolean; searchable: boolean }[] = [
  { key: 'contentType', label: 'Content type', startOpen: true, searchable: false },
  { key: 'topic', label: 'Topic', startOpen: true, searchable: true },
  { key: 'audience', label: 'Audience', startOpen: false, searchable: false },
  { key: 'clinicalArea', label: 'Clinical area', startOpen: false, searchable: false },
  { key: 'format', label: 'Format', startOpen: false, searchable: false },
];

interface CatalogFacetSidebarProps {
  facets: CatalogFacets;
  selected: Record<CatalogFacetKey, string[]>;
  programs?: ProgramFacetOption[];
  selectedPrograms?: string[];
  onToggle: (facet: CatalogFacetKey, value: string) => void;
  onToggleProgram?: (slug: string) => void;
  onClearFilters: () => void;
  pending?: boolean;
}

function facetEntries(counts: Record<string, number>, selected: string[]) {
  const values = new Set([...Object.keys(counts), ...selected]);
  return [...values]
    .map((value) => ({ value, count: counts[value] ?? 0 }))
    .sort((a, b) => b.count - a.count || catalogLabel(a.value).localeCompare(catalogLabel(b.value)));
}

export default function CatalogFacetSidebar({
  facets,
  selected,
  programs = [],
  selectedPrograms = [],
  onToggle,
  onToggleProgram,
  onClearFilters,
  pending = false,
}: CatalogFacetSidebarProps) {
  const [expanded, setExpanded] = useState<Record<CatalogFacetKey, boolean>>(() => {
    const initial = {} as Record<CatalogFacetKey, boolean>;
    for (const group of GROUPS) {
      initial[group.key] = group.startOpen || selected[group.key].length > 0;
    }
    return initial;
  });
  const [programOpen, setProgramOpen] = useState(selectedPrograms.length > 0);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [topicQuery, setTopicQuery] = useState('');

  const active = useMemo(() => {
    const chips: { facet: CatalogFacetKey; value: string }[] = [];
    for (const key of CATALOG_FACET_KEYS) {
      for (const value of selected[key]) chips.push({ facet: key, value });
    }
    return chips;
  }, [selected]);

  const topicEntries = facetEntries(facets.topic, selected.topic);
  const visibleTopics = useMemo(() => {
    const query = topicQuery.trim().toLowerCase();
    const matched = query
      ? topicEntries.filter((entry) => catalogLabel(entry.value).toLowerCase().includes(query) || entry.value.toLowerCase().includes(query))
      : topicEntries;
    if (query || showAllTopics) return matched;
    const top = matched.slice(0, 12);
    const selectedMissing = matched.filter(
      (entry) => selected.topic.includes(entry.value) && !top.some((item) => item.value === entry.value)
    );
    return [...top, ...selectedMissing];
  }, [topicEntries, topicQuery, showAllTopics, selected.topic]);

  const showPrograms = programs.length > 0 || selectedPrograms.length > 0;

  return (
    <aside className="w-full md:w-64 lg:w-80 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
      {(active.length > 0 || selectedPrograms.length > 0) && (
        <div className="pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">Active filters</p>
            <button
              type="button"
              onClick={onClearFilters}
              className="text-sm text-primary hover:text-primary-dark font-medium"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedPrograms.map((slug) => {
              const program = programs.find((item) => item.slug === slug);
              if (!program && programs.length === 0) return null;
              const label = program?.title ?? slug;
              return (
                <Badge key={`program-${slug}`} variant="default" className="flex items-center gap-1">
                  {label}
                  <button
                    type="button"
                    onClick={() => onToggleProgram?.(slug)}
                    className="ml-1 hover:text-gray-900"
                    aria-label={`Remove ${label} filter`}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
            {active.map((chip) => {
              const label = catalogLabel(chip.value);
              return (
                <Badge key={`${chip.facet}-${chip.value}`} variant="default" className="flex items-center gap-1">
                  {label}
                  <button
                    type="button"
                    onClick={() => onToggle(chip.facet, chip.value)}
                    className="ml-1 hover:text-gray-900"
                    aria-label={`Remove ${label} filter`}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {GROUPS.map((group) => {
        const entries = group.key === 'topic'
          ? visibleTopics
          : facetEntries(facets[group.key], selected[group.key]);
        const isOpen = expanded[group.key];
        return (
          <Fragment key={group.key}>
          <div className="border-b border-gray-200 pb-4">
            <button
              type="button"
              onClick={() => setExpanded((current) => ({ ...current, [group.key]: !current[group.key] }))}
              className="flex items-center justify-between w-full mb-3"
              aria-expanded={isOpen}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{group.label}</span>
                {selected[group.key].length > 0 && (
                  <span className="text-xs bg-primary text-white rounded-full px-2 py-0.5">
                    {selected[group.key].length}
                  </span>
                )}
              </span>
              {isOpen ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {isOpen && (
              <div className="space-y-2">
                {group.searchable && (
                  <Input
                    type="text"
                    placeholder="Find a topic"
                    value={topicQuery}
                    onChange={(event) => setTopicQuery(event.target.value)}
                    aria-label="Find a topic"
                  />
                )}
                {entries.length === 0 ? (
                  pending ? (
                    <p className="text-sm text-gray-500">Loading filters</p>
                  ) : (
                    <p className="text-sm text-gray-500">No matches</p>
                  )
                ) : (
                  entries.map((entry) => {
                    const label = catalogLabel(entry.value);
                    const checked = selected[group.key].includes(entry.value);
                    return (
                      <label
                        key={entry.value}
                        className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggle(group.key, entry.value)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700 flex-1">{label}</span>
                        <span className="text-xs text-gray-500">({entry.count})</span>
                      </label>
                    );
                  })
                )}
                {group.key === 'topic' && !topicQuery && topicEntries.length > 12 && (
                  <button
                    type="button"
                    onClick={() => setShowAllTopics((current) => !current)}
                    className="text-sm text-primary hover:text-primary-dark font-medium w-full text-left pt-2"
                  >
                    {showAllTopics ? 'Show less' : `Show all ${topicEntries.length} topics`}
                  </button>
                )}
              </div>
            )}
          </div>
          {group.key === 'topic' && showPrograms && (
            <div className="border-b border-gray-200 pb-4">
              <button
                type="button"
                onClick={() => setProgramOpen((current) => !current)}
                className="flex items-center justify-between w-full mb-3"
                aria-expanded={programOpen}
              >
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Program</span>
                  {selectedPrograms.length > 0 && (
                    <span className="text-xs bg-primary text-white rounded-full px-2 py-0.5">
                      {selectedPrograms.length}
                    </span>
                  )}
                </span>
                {programOpen ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {programOpen && (
                <div className="space-y-2">
                  {programs.length === 0 ? (
                    <p className="text-sm text-gray-500">Loading filters</p>
                  ) : programs.map((program) => {
                    const checked = selectedPrograms.includes(program.slug);
                    return (
                      <label
                        key={program.slug}
                        className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleProgram?.(program.slug)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700 flex-1">{program.title}</span>
                        {program.count > 0 && (
                          <span className="text-xs text-gray-500">({program.count})</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </Fragment>
        );
      })}
    </aside>
  );
}
